import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PLATFORM_COLORS } from "../lib/constants";
import { SponsorBanner } from "../components/SponsorBanner";
import { Loader2, TrendingUp, Repeat, Trophy, Crosshair, Radio } from "lucide-react";

const COLORS = ["#00F0FF", "#FF2A55", "#00FF87", "#FFB800", "#A855F7", "#3B82F6", "#F472B6", "#22D3EE"];

const DistBar = ({ data, title, testid }) => {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map((e) => e[1]));
  return (
    <div data-testid={testid} className="rounded-xl border border-[#1E293F] bg-[#0F1424] p-5">
      <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-wider text-[#94A3B8]">{title}</h3>
      <div className="space-y-3">
        {entries.map(([label, val], i) => (
          <div key={label}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="text-[#CBD5E1]">{label}</span>
              <span className="font-mono font-bold text-white">{val}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-[#141B2D]">
              <div className="h-full rounded-full transition-all" style={{ width: `${(val / max) * 100}%`, backgroundColor: COLORS[i % COLORS.length] }} />
            </div>
          </div>
        ))}
        {entries.length === 0 && <p className="text-sm text-[#64748B]">No data yet.</p>}
      </div>
    </div>
  );
};

export default function Analytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [hunters, setHunters] = useState([]);

  useEffect(() => {
    api.get("/analytics").then(({ data }) => setData(data)).catch(() => {});
    api.get("/leaderboard").then(({ data }) => setHunters(data.hunters || [])).catch(() => {});
  }, []);

  if (!data) return <div className="flex justify-center py-32 text-[#64748B]"><Loader2 className="animate-spin" /></div>;

  const maxTrend = Math.max(1, ...data.trend.map((t) => t.count));

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <div className="animate-fade-up">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#00F0FF]">
          <TrendingUp size={13} /> Community Analytics
        </div>
        <h1 className="mt-4 font-heading text-3xl font-black tracking-tight text-white sm:text-4xl">Cheater Stats Dashboard</h1>
        <p className="mt-2 text-[#94A3B8]">Where the cheating is worst, what offenses trend, and who the community reports most.</p>
        {data.streamers_busted > 0 && (
          <div data-testid="streamers-busted-stat" className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#9146FF]/40 bg-[#9146FF]/10 px-4 py-2">
            <Radio size={15} className="text-[#C4B5FD]" />
            <span className="font-heading text-sm font-bold text-white">🎥 {data.streamers_busted} streamer{data.streamers_busted === 1 ? "" : "s"} busted</span>
            <span className="text-xs text-[#C4B5FD]">— cheating on stream, caught on their own VODs</span>
          </div>
        )}
      </div>

      <div className="mt-6"><SponsorBanner /></div>

      {/* Trend */}
      <div className="animate-fade-up mt-8 rounded-xl border border-[#1E293F] bg-[#0F1424] p-5" data-testid="trend-chart">
        <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-wider text-[#94A3B8]">Reports — last 14 days</h3>
        <div className="flex h-40 items-end gap-1.5">
          {data.trend.map((t) => (
            <div key={t.date} className="group relative flex flex-1 flex-col items-center justify-end">
              <div className="w-full rounded-t bg-gradient-to-t from-[#00F0FF]/40 to-[#00F0FF] transition-all hover:from-[#FF2A55]/40 hover:to-[#FF2A55]" style={{ height: `${(t.count / maxTrend) * 100}%`, minHeight: t.count ? "4px" : "0" }} />
              <span className="mt-1 text-[9px] text-[#64748B]">{t.date.slice(5)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="animate-fade-up mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
        <DistBar data={data.by_platform} title="By Platform" testid="dist-platform" />
        <DistBar data={data.by_cheat} title="By Offense" testid="dist-cheat" />
        <DistBar data={data.by_mode} title="By Game Mode" testid="dist-mode" />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Leaderboard */}
        <div className="animate-fade-up rounded-xl border border-[#1E293F] bg-[#0F1424] p-5" data-testid="leaderboard">
          <h3 className="mb-1 flex items-center gap-2 font-heading text-lg font-bold text-white"><Trophy size={18} className="text-[#FFB800]" /> Most Reported Players</h3>
          <p className="mb-4 text-xs text-[#64748B]">"Faced" = how many players confirmed they ran into this cheater (clicked "I faced them too").</p>
          <div className="space-y-2">
            {data.top_players.map((p, i) => (
              <button
                key={`${p.platform}-${p.gamertag}`}
                data-testid={`leaderboard-row-${i}`}
                onClick={() => navigate(`/players/${p.platform}/${encodeURIComponent(p.gamertag)}`)}
                className="flex w-full items-center gap-3 rounded-lg border border-[#1E293F] bg-[#141B2D] px-3 py-2.5 text-left transition-colors hover:border-[#00F0FF]/40"
              >
                <span className="w-6 font-heading text-lg font-black text-[#64748B]">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold text-white">{p.gamertag}</span>
                    {p.banned && <span className="rounded bg-[#FF2A55]/15 px-1.5 py-0.5 text-[10px] font-bold text-[#FF2A55]">CHEATER</span>}
                    {p.is_serial && <Repeat size={12} className="text-[#FF2A55]" />}
                  </div>
                  <span className="text-xs" style={{ color: PLATFORM_COLORS[p.platform] }}>{p.platform}</span>
                </div>
                <div className="text-right" title="Players who confirmed they faced this cheater">
                  <div className="font-mono font-bold text-[#FF2A55]">{p.faced}</div>
                  <div className="text-[10px] uppercase text-[#64748B]">faced it</div>
                </div>
              </button>
            ))}
            {data.top_players.length === 0 && <p className="text-sm text-[#64748B]">No players yet.</p>}
          </div>
        </div>

        {/* Serial offenders */}
        <div className="animate-fade-up rounded-xl border border-[#FF2A55]/30 bg-[#0F1424] p-5" data-testid="serial-offenders">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-white"><Crosshair size={18} className="text-[#FF2A55]" /> Serial Offenders</h3>
          <p className="mb-3 text-xs text-[#94A3B8]">Players reported 2+ times — auto-flagged for priority review.</p>
          <div className="space-y-2">
            {data.serial_offenders.map((p) => (
              <button
                key={`${p.platform}-${p.gamertag}`}
                data-testid={`serial-row-${p.gamertag}`}
                onClick={() => navigate(`/players/${p.platform}/${encodeURIComponent(p.gamertag)}`)}
                className="flex w-full items-center gap-3 rounded-lg border border-[#FF2A55]/20 bg-[#FF2A55]/5 px-3 py-2.5 text-left transition-colors hover:border-[#FF2A55]/50"
              >
                <div className="min-w-0 flex-1">
                  <span className="truncate font-semibold text-white">{p.gamertag}</span>
                  <span className="ml-2 text-xs" style={{ color: PLATFORM_COLORS[p.platform] }}>{p.platform}</span>
                </div>
                <span className="rounded-full bg-[#FF2A55] px-2.5 py-1 font-mono text-xs font-bold text-white">{p.reports}× reported</span>
              </button>
            ))}
            {data.serial_offenders.length === 0 && <p className="text-sm text-[#64748B]">No serial offenders detected yet.</p>}
          </div>
        </div>
      </div>

      {/* Certified Hunters */}
      <div className="animate-fade-up mt-8 rounded-xl border border-[#00FF87]/25 bg-[#0F1424] p-5" data-testid="hunter-leaderboard">
        <h3 className="mb-1 flex items-center gap-2 font-heading text-lg font-bold text-white"><Crosshair size={18} className="text-[#00FF87]" /> Certified Hunters</h3>
        <p className="mb-4 text-xs text-[#64748B]">Reporters whose call-outs got confirmed as cheaters by the mod team. Respect the hunters keeping lobbies clean.</p>
        {hunters.length === 0 ? (
          <p className="text-sm text-[#64748B]">No confirmed busts yet — be the first to earn Certified Hunter.</p>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {hunters.map((h, i) => (
              <button
                key={`${h.name}-${i}`}
                data-testid={`hunter-row-${i}`}
                onClick={() => h.user_id && navigate(`/hunters/${h.user_id}`)}
                className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${i === 0 ? "border-[#00FF87]/50 bg-[#00FF87]/[0.08]" : "border-[#1E293F] bg-[#141B2D]"} hover:border-[#00FF87]/60`}
              >
                <span className={`w-6 font-heading text-lg font-black ${i === 0 ? "text-[#00FF87]" : "text-[#64748B]"}`}>{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-white">{h.name}</div>
                  {h.tier && <div className="text-[11px] font-bold uppercase tracking-wide text-[#94A3B8]">{h.tier}</div>}
                </div>
                <span className="shrink-0 rounded-full bg-[#00FF87]/15 px-2.5 py-1 font-mono text-xs font-bold text-[#00FF87]">{h.busts} bust{h.busts === 1 ? "" : "s"}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
